import type { Signal } from "@preact/signals-core";
import { currentScope } from "./scope";

export interface SelectOption {
  value: string;
  label: string;
}

/** A labelled `<optgroup>` of options — e.g. projects grouped by client on the
 * timesheet quick-add picker (03-api-spec.md § Timesheets). */
export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

function isOptionGroup(entry: SelectOption | SelectOptionGroup): entry is SelectOptionGroup {
  return "options" in entry;
}

function appendOption(parent: HTMLSelectElement | HTMLOptGroupElement, option: SelectOption): void {
  const optionEl = document.createElement("option");
  optionEl.value = option.value;
  optionEl.textContent = option.label;
  parent.appendChild(optionEl);
}

/** The one sanctioned way to fill a `<select>` with dynamic data (Phase 2a item 1):
 * options are built as DOM elements with `textContent`, so a label can never become
 * markup — string-concatenated `innerHTML` option lists shipped stored XSS in Phase 2
 * and are now banned by lint. Preserves the current selection when it still exists.
 * Accepts a flat option list or a mix of options and `SelectOptionGroup`s, rendered as
 * `<optgroup>`s in place (Phase 2b: the client-grouped project picker). */
export function setSelectOptions(
  select: HTMLSelectElement,
  options: (SelectOption | SelectOptionGroup)[],
  opts: { placeholder?: string } = {},
): void {
  const previous = select.value;
  select.replaceChildren();
  if (opts.placeholder !== undefined) {
    const placeholderEl = document.createElement("option");
    placeholderEl.value = "";
    placeholderEl.textContent = opts.placeholder;
    select.appendChild(placeholderEl);
  }
  const allValues: string[] = [];
  for (const entry of options) {
    if (isOptionGroup(entry)) {
      const groupEl = document.createElement("optgroup");
      groupEl.label = entry.label;
      for (const option of entry.options) {
        appendOption(groupEl, option);
        allValues.push(option.value);
      }
      select.appendChild(groupEl);
    } else {
      appendOption(select, entry);
      allValues.push(entry.value);
    }
  }
  if (previous && allValues.includes(previous)) select.value = previous;
}

/** bindValue only targets input/textarea; a <select>'s change event needs its own wiring. */
export function bindSelect<T extends string>(select: HTMLSelectElement, field: Signal<T>): void {
  currentScope().effect(() => {
    select.value = field.value;
  });
  select.addEventListener("change", () => {
    field.value = select.value as T;
  });
}

export function bindNullableSelect<T extends string>(
  select: HTMLSelectElement,
  field: Signal<T | null>,
): void {
  currentScope().effect(() => {
    select.value = field.value ?? "";
  });
  select.addEventListener("change", () => {
    field.value = (select.value === "" ? null : select.value) as T | null;
  });
}

/** bindValue is typed for string signals; several fields across the app are `string | null`. */
export function bindNullableValue(
  input: HTMLInputElement | HTMLTextAreaElement,
  field: Signal<string | null>,
): void {
  currentScope().effect(() => {
    const formatted = field.value ?? "";
    if (document.activeElement !== input && input.value !== formatted) input.value = formatted;
  });
  input.addEventListener("input", () => {
    field.value = input.value === "" ? null : input.value;
  });
}

export function bindNumberValue(input: HTMLInputElement, field: Signal<number>): void {
  currentScope().effect(() => {
    const formatted = String(field.value);
    if (document.activeElement !== input && input.value !== formatted) input.value = formatted;
  });
  input.addEventListener("input", () => {
    const parsed = Number(input.value);
    if (!Number.isNaN(parsed)) field.value = parsed;
  });
}

export function bindCheckbox(input: HTMLInputElement, field: Signal<boolean>): void {
  currentScope().effect(() => {
    input.checked = field.value;
  });
  input.addEventListener("change", () => {
    field.value = input.checked;
  });
}
